import { ACK } from '../schemas/wa/message-ack';

export type MessageStatus =
  | 'before-wa-send' | 'pending'
  | 'sent' | 'sent_pending_ack' | 'bt-sent' | 'confirmed'
  | 'failed' | 'error' | 'cancelled' | 'expired';

export type WireState = 'queued' | 'sent' | 'failed';
export type ReceiptState = 'none' | 'delivered' | 'read' | 'not_reported';
export interface StatusAxes { wire: WireState; receipt: ReceiptState; }
export interface AckData {
  deliveryRemaining: number; readRemaining: number; playedRemaining: number; totalRecipients: number;
}

/** Receipt axis from the monotonic ack integer. 0/1 → none, 2 → delivered,
 *  3/4 → read. No default-to-played path: anything else → none. */
export function receiptStateFromAck(ack?: number): 'none' | 'delivered' | 'read' {
  if (ack === ACK.DEVICE) return 'delivered';
  if (ack === ACK.READ || ack === ACK.PLAYED) return 'read';
  return 'none';
}

const WIRE_BY_STATUS: Record<string, WireState> = {
  'before-wa-send': 'queued', 'pending': 'queued',
  'sent': 'sent', 'sent_pending_ack': 'sent', 'bt-sent': 'sent', 'confirmed': 'sent',
  // Receipt-bearing + in-flight statuses: legacy/in-flight rows in production
  // (40,019 messagelogs rows as of 2026-07) that DID reach the wire — they must
  // not fall to the 'queued' default (which rendered a delivered/read message
  // as "Queued" in the badge and miscounted it in the header).
  'delivered': 'sent', 'read': 'sent', 'played': 'sent', 'received': 'sent', 'sending': 'sent',
  // 'expired' is a first-class TERMINAL status the backend persists to
  // messagelogs.status (per-message send-window expiry, send-window.ts) — a
  // give-up, so it maps to 'failed', NOT the 'queued' default (which would keep
  // the campaign "still sending" forever and render the row as "Queued").
  'failed': 'failed', 'error': 'failed', 'cancelled': 'failed', 'expired': 'failed',
};

/** Wire axis from persisted status. Unknown → 'queued' (safe, non-terminal). */
export function wireStateFromStatus(status: string): WireState {
  return WIRE_BY_STATUS[status] ?? 'queued';
}

/** Receipt axis from the persisted status string itself — the legacy path for
 *  rows that carry the receipt in `status` rather than (or in addition to)
 *  `ack`. 'delivered'/'received' → 'delivered'; 'read'/'played' → 'read';
 *  everything else → 'none'. */
export function receiptStateFromStatus(status: string): 'none' | 'delivered' | 'read' {
  if (status === 'delivered' || status === 'received') return 'delivered';
  if (status === 'read' || status === 'played') return 'read';
  return 'none';
}

// Receipt-axis rank (higher = more advanced). 'delivered'/'read' are never
// mixed with 'not_reported' in practice — isGroup is constant per message, so
// a message's rows are either all-group or all-1:1 — but callers (e.g. the FE
// cross-row aggregator) fold 'not_reported' against a 'none' seed, so it must
// outrank 'none': a row that reached the wire as a group member is a more
// advanced receipt state than a row that hasn't reached the wire at all.
const RECEIPT_RANK: Record<ReceiptState, number> = {
  'none': 0,
  'not_reported': 1,
  'delivered': 2,
  'read': 3,
};

/** The more-advanced of two receipt states (read > delivered > none). */
export function maxReceipt(a: ReceiptState, b: ReceiptState): ReceiptState {
  return RECEIPT_RANK[b] > RECEIPT_RANK[a] ? b : a;
}

/** Two-axis projection. A group that reached the wire always shows receipt
 *  'not_reported' — WhatsApp gives no reliable per-member group receipts.
 *  For 1:1, the receipt is the more-advanced of the ack-derived and the
 *  status-derived receipt — legacy rows carry the receipt in `status` with no
 *  `ack`, current rows carry it in `ack`; either can be the source of truth
 *  for a given row. */
export function logToAxes(status: string, ack: number | undefined, isGroup: boolean): StatusAxes {
  const wire = wireStateFromStatus(status);
  if (wire !== 'sent') return { wire, receipt: 'none' };
  if (isGroup) return { wire, receipt: 'not_reported' };
  return { wire, receipt: maxReceipt(receiptStateFromAck(ack), receiptStateFromStatus(status)) };
}

export type PrimaryStatus = 'queued' | 'sent' | 'confirmed' | 'failed';
export interface DerivedMessageStatus { primary: PrimaryStatus; receipt: ReceiptState; }

// Legacy DB rows that carry a receipt IN `status` (no separate primary). They
// DID reach the server, so the reader folds them to the 'confirmed' primary
// (the receipt axis, via logToAxes, still records delivered/read).
const LEGACY_RECEIPT_STATUS = new Set(['delivered', 'read', 'played', 'received']);

/** THE canonical read-side reducer: a message's (raw status, numeric ack) → the
 *  first-class PRIMARY rung + the separate receipt axis. `confirmed` = WhatsApp
 *  server got it (ack>=SERVER or status 'confirmed'); legacy ack-as-status rows
 *  fold to confirmed. delivered/read/played are the receipt axis, never primary. */
export function deriveMessageStatus(status: string, ack: number | undefined, isGroup = false): DerivedMessageStatus {
  const { wire, receipt } = logToAxes(status, ack, isGroup);
  let primary: PrimaryStatus;
  if (wire === 'failed') primary = 'failed';
  else if (wire === 'queued') primary = 'queued';
  else {
    const confirmed = status === 'confirmed'
      || (typeof ack === 'number' && ack >= ACK.SERVER)
      || LEGACY_RECEIPT_STATUS.has(status);
    primary = confirmed ? 'confirmed' : 'sent';
  }
  return { primary, receipt };
}

// THE ONE wire-axis rank (lower = more advanced). Receipt states are absent by
// design, so they fall to the unknown floor and can never win a row pick.
const RANK: Record<string, number> = {
  'confirmed': 1.5,
  'sent_pending_ack': 2.9,
  'sent': 3, 'bt-sent': 3,
  'failed': 3.5, 'error': 3.5, 'cancelled': 3.5, 'expired': 3.5,
  'pending': 4, 'before-wa-send': 4,
};
export function statusRank(status: string): number {
  return RANK[status] ?? 99;
}

/** Rebuild the *Remaining detail from an ack (relocated, was
 *  MessageLogUpdateService.convertWWebJsAckToAckData). Kept for the timeline
 *  view + the recordDeliveryAck payload. */
export function ackDataFromAck(ack: number, totalRecipients = 1): AckData {
  const all = { deliveryRemaining: totalRecipients, readRemaining: totalRecipients, playedRemaining: totalRecipients, totalRecipients };
  if (ack === ACK.DEVICE) return { ...all, deliveryRemaining: 0 };
  if (ack === ACK.READ) return { ...all, deliveryRemaining: 0, readRemaining: 0 };
  if (ack === ACK.PLAYED) return { deliveryRemaining: 0, readRemaining: 0, playedRemaining: 0, totalRecipients };
  return all; // 0/1/unknown → nothing delivered yet
}
